import React from "react";
import { spring, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { SvgIcon } from "../../components/SvgComponents";

interface IntroProps {
  headline: string;
  keywords: string[];
  introIcon?: string;
  frame: number;
  fps: number;
}

export const CinematicEpicIntro: React.FC<IntroProps> = ({
  headline,
  keywords,
  introIcon,
  frame,
  fps,
}) => {
  const { width, height } = useVideoConfig();

  // Spring animations for text scale and opacity
  const textScale = spring({
    frame,
    fps,
    from: 0.85,
    to: 1.0,
    config: { damping: 15, mass: 0.6, stiffness: 100 },
  });

  const textOpacity = interpolate(frame, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Letterbox animations (slow slide in)
  const letterboxHeight = interpolate(frame, [0, 20], [height * 0.15, height * 0.12], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Icon entry animation (delayed spring)
  const iconFrame = Math.max(0, frame - 8);
  const iconScale = spring({
    frame: iconFrame,
    fps,
    from: 0,
    to: 1,
    config: { damping: 12, mass: 0.5, stiffness: 130 },
  });

  const iconRotate = interpolate(iconFrame, [0, 30], [-10, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Cinematic Widescreen Letterbox Bars */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: letterboxHeight,
          background: "linear-gradient(to bottom, #000000 70%, transparent 100%)",
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: letterboxHeight,
          background: "linear-gradient(to top, #000000 70%, transparent 100%)",
          zIndex: 10,
        }}
      />

      {/* Main content container */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "35px",
          width: "100%",
          padding: "0 40px",
          boxSizing: "border-box",
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          zIndex: 5,
        }}
      >
        {/* Intro SVG Illustration */}
        <div
          style={{
            transform: `scale(${iconScale}) rotate(${iconRotate}deg)`,
            filter: "drop-shadow(0 15px 30px rgba(255, 140, 0, 0.45))",
            marginBottom: "10px",
            background: "rgba(255, 140, 0, 0.05)",
            borderRadius: "50%",
            padding: "24px",
            border: "1px solid rgba(255, 140, 0, 0.2)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SvgIcon
            name={introIcon || "sparkles"}
            width={160}
            height={160}
            primaryColor="#ff8c00"
          />
        </div>

        {/* Cinematic Label */}
        <div
          style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "12px",
            fontWeight: 800,
            color: "#ff8c00",
            letterSpacing: "8px",
            textTransform: "uppercase",
            textShadow: "0 0 10px rgba(255,140,0,0.5)",
          }}
        >
          — BREAKING INSIGHT —
        </div>

        {/* Large Headline */}
        <h1
          style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "58px",
            fontWeight: 900,
            color: "#ffffff",
            lineHeight: 1.15,
            margin: 0,
            letterSpacing: "-1.5px",
            textTransform: "uppercase",
          }}
        >
          {headline}
        </h1>

        {/* Film strip decorative divider */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ width: "80px", height: "1px", background: "linear-gradient(to left, #ff8c00, transparent)" }} />
          <div style={{ width: "8px", height: "8px", border: "1px solid #ff8c00", transform: "rotate(45deg)" }} />
          <div style={{ width: "80px", height: "1px", background: "linear-gradient(to right, #ff8c00, transparent)" }} />
        </div>

        {/* Keywords tags */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {keywords.slice(0, 3).map((kw, i) => (
            <span
              key={i}
              style={{
                fontFamily: "Space Grotesk, sans-serif",
                fontSize: "10px",
                fontWeight: 700,
                color: "#ff8c00",
                background: "rgba(255, 140, 0, 0.1)",
                border: "1px solid rgba(255, 140, 0, 0.3)",
                padding: "4px 12px",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              {kw}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
